import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  TableSortLabel,
  Paper,
} from "@material-ui/core";
import { TablePaginationActions } from "./TablePaginationActions";
import { EditNotesDialog } from "./EditNotesDialog";
import { formatDate } from "../../../utils/Format";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 450,
  },
  cell: {
    verticalAlign: "top",
    padding: theme.spacing(1),
    borderRight: "1px solid rgba(224, 224, 224, 1)",
    "&:last-child": { borderRight: "none" },
  },
  headerCell: {
    backgroundColor: "#0c1c7d",
    color: "white",
    borderRight: "1px solid rgba(255, 255, 255, 1)",
    "&:last-child": { borderRight: "none" },
  },
  tableBody: {
    "& tr:nth-of-type(odd)": { backgroundColor: "#fafafa" },
    "& tr:nth-of-type(even)": { backgroundColor: "white" },
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  editButton: {
    color: "darkblue",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
  },
  dateColumn: { width: "6rem" },
  authorColumn: { width: "8rem" },
  noteColumn: { width: "auto", flexGrow: 1 },
  actionColumn: { width: "4rem" },
}));

// Table column configuration
const columns = [
  {
    id: "createdAt",
    label: "Creation Date",
    className: "dateColumn",
    sortable: true,
  },
  {
    id: "createdBy",
    label: "Author",
    className: "authorColumn",
    sortable: true,
  },
  {
    id: "updatedAt",
    label: "Last Update",
    className: "dateColumn",
    sortable: true,
  },
  { id: "noteText", label: "Note", className: "noteColumn", sortable: true },
  {
    id: "actions",
    label: "Action",
    className: "actionColumn",
    sortable: false,
  },
];

function getSortedData(data, order, orderBy) {
  const isAscending = order === "asc";

  return [...data].sort((a, b) => {
    // Handle null/undefined values
    const valueA = a[orderBy] == null ? "" : String(a[orderBy]);
    const valueB = b[orderBy] == null ? "" : String(b[orderBy]);

    // Use localeCompare with options for robust comparison
    const comparisonResult = valueA.localeCompare(valueB, undefined, {
      numeric: true, // So "10" comes after "2", not after "1"
      sensitivity: "base", // Ignore case and accents
    });

    // Clear if/else logic for readability
    if (comparisonResult < 0) {
      return isAscending ? -1 : 1;
    }
    if (comparisonResult > 0) {
      return isAscending ? 1 : -1;
    }
    return 0; // Values are equal
  });
}

function UserAccountNotesTable({
  noteList = [],
  currentUser,
  selectedUser,
  onRefreshTable,
  onSaveNote,
}) {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  // Memoize sorted data to avoid unnecessary re-sorting
  const sortedNotes = useMemo(
    () => getSortedData(noteList, order, orderBy),
    [noteList, order, orderBy]
  );

  const displayedNotes = sortedNotes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Event handlers
  const handleEditNote = (note) => {
    setSelectedNote(note);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleNoteSaved = (note, updatedNoteText) => {
    onSaveNote(note, updatedNoteText);
    onRefreshTable();
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Preserve original sort handler approach to maintain animation
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Create sort handler for a column - this preserves the animation
  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  // Extracted pagination component
  const Pagination = ({ position }) => (
    <TablePagination
      rowsPerPageOptions={[5, 10, 25, 50, 100]}
      component={position === "top" ? "div" : undefined}
      colSpan={position === "bottom" ? columns.length : undefined}
      count={noteList.length}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      ActionsComponent={TablePaginationActions}
    />
  );

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Pagination position="top" />

        <TableContainer>
          <Table
            className={classes.table}
            size="small"
            aria-label="User Account Notes"
          >
            {/* Table Header with Sort Labels */}
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={`${classes.cell} ${classes.headerCell} ${
                      classes[column.className]
                    }`}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={createSortHandler(column.id)}
                      >
                        {column.label}
                        {orderBy === column.id ? (
                          <span className={classes.visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody className={classes.tableBody}>
              {displayedNotes.length > 0 ? (
                displayedNotes.map((note) => (
                  <TableRow key={note.userNoteId} hover>
                    <TableCell
                      className={`${classes.cell} ${classes.dateColumn}`}
                    >
                      {formatDate(new Date(note.createdAt))}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.authorColumn}`}
                    >
                      {note.createdBy}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.dateColumn}`}
                    >
                      {formatDate(new Date(note.updatedAt))}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.noteColumn}`}
                    >
                      {note.noteText}
                    </TableCell>
                    <TableCell
                      className={`${classes.cell} ${classes.actionColumn}`}
                    >
                      {note.createdBy === currentUser.userNm && (
                        <button
                          type="button"
                          className={classes.editButton}
                          onClick={() => handleEditNote(note)}
                        >
                          Edit
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No notes available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <Pagination position="bottom" />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <EditNotesDialog
        open={dialogOpen}
        onNoteSaved={handleNoteSaved}
        onClose={handleCloseDialog}
        note={selectedNote}
        selectedUser={selectedUser}
      />
    </div>
  );
}

export { UserAccountNotesTable };
